import './Account.scss'

import { clearRecentItemList } from 'data/reducer/search'
import { useAppDispatch, useAppSelector } from 'data/store/hooks'
import React, { useEffect, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'

import { RootState } from '../../data/reducer'
import { AvatarButton } from './components/AvatarButton'
import { RefreshIFrame } from './components/RefreshIFrame'
import { Units } from './components/Units/Units'
import { UserFeedback } from './components/UserFeedback'
import { UserMenu } from './components/UserMenu'
import { handleToken, signIn, signOut, signUp } from './data/reducer'
import { useOutsideHandler } from './hooks/useOutsideClick.hook'
import { RefreshIFrameMessage } from './lib/types'
import { createRefreshMessage, createStartStopMessage } from './lib/utils'

export function Account(): JSX.Element {
  const [isMenuVisible, setMenuVisible] = useState(false)
  const [isFeedbackVisible, setFeedbackVisible] = useState(false)
  const [isUnitsVisible, setUnitsVisible] = useState(false)

  const userMenuRef = useRef(null)
  const feedbackRef = useRef(null)
  const unitsRef = useRef(null)

  const { hash, pathname, search } = useLocation()
  const refreshIFrameRef = useRef<HTMLIFrameElement>(null)
  const dispatch = useAppDispatch()
  const { user } = useAppSelector((state: RootState) => state.account)
  const locationState = { hash, pathName: pathname, search }

  useEffect(() => {
    refreshIFrameRef.current?.contentWindow?.postMessage(createStartStopMessage(user), '*')
  }, [user])

  useEffect(() => {
    const listener = (event: MessageEvent<RefreshIFrameMessage>): void => {
      if (event.data.source === 'ha-refresh-iframe') {
        dispatch(handleToken(event.data.hash))
      }
    }
    window.addEventListener('message', listener)
    return () => window.removeEventListener('message', listener)
  }, [dispatch])

  useEffect(() => {
    const iFrame = refreshIFrameRef.current
    const postRefreshMessage = (): void => {
      iFrame?.removeEventListener('load', postRefreshMessage)
      iFrame?.contentWindow?.postMessage(createRefreshMessage(), '*')
    }
    iFrame?.addEventListener('load', postRefreshMessage)
    return () => iFrame?.removeEventListener('load', postRefreshMessage)
  }, [])

  useOutsideHandler(userMenuRef, () => {
    if (isMenuVisible) {
      setMenuVisible(false)
    }
  })

  useOutsideHandler(feedbackRef, () => {
    if (isFeedbackVisible) {
      setFeedbackVisible(false)
    }
  })

  useOutsideHandler(unitsRef, () => {
    if (isUnitsVisible) {
      setUnitsVisible(false)
    }
  })

  function openMenu(): void {
    setMenuVisible(true)
  }

  return (
    <div className="Account__container">
      <RefreshIFrame ref={refreshIFrameRef} />
      <AvatarButton user={user} onClick={openMenu} />
      {isMenuVisible && (
        <UserMenu
          user={user}
          onClick={() => setMenuVisible(false)}
          onFeedback={() => {
            setMenuVisible(false)
            setFeedbackVisible(true)
          }}
          onSignUp={() => {
            setMenuVisible(false)
            dispatch(signUp({ ...locationState, action: 'sign-up' }))
          }}
          onSignIn={() => {
            setMenuVisible(false)
            dispatch(signIn({ ...locationState, action: 'sign-in' }))
          }}
          onSignOut={() => {
            setMenuVisible(false)
            dispatch(signOut())
          }}
          onUnits={() => {
            setMenuVisible(false)
            setUnitsVisible(true)
          }}
          onClearHistory={() => {
            setMenuVisible(false)
            dispatch(clearRecentItemList('settings'))
          }}
          ref={userMenuRef}
        />
      )}
      {isFeedbackVisible && (
        <UserFeedback ref={feedbackRef} onClick={() => setFeedbackVisible(false)} user={user} />
      )}
      {isUnitsVisible && <Units ref={unitsRef} onClose={() => setUnitsVisible(false)} />}
    </div>
  )
}
